import { Crown, Lock, ArrowRight, ChevronDown } from "lucide-react";
import { useLang } from "../context/LangContext";
import { IMAGES } from "../images";
import { waLink } from "../constants";
import { Reveal } from "./Reveal";
import { Countdown } from "./Countdown";

export const Campaign = () => {
  const { t, lang } = useLang();
  const c = t.campaign;

  return (
    <section id="campaign" className="relative py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div
            data-testid="campaign-banner"
            className="relative overflow-hidden rounded-[28px] border border-white/10 bg-gradient-to-b from-navy-800/80 via-navy-900/70 to-navy-950/90 p-8 sm:p-12 lg:p-16"
          >
            {/* glows */}
            <div className="animate-float-slow pointer-events-none absolute -left-24 top-0 h-[340px] w-[340px] rounded-full bg-violet-600/25 blur-[110px]" />
            <div className="animate-float-slow pointer-events-none absolute -right-24 bottom-0 h-[340px] w-[340px] rounded-full bg-blue-600/25 blur-[110px]" />
            <div className="bg-grid pointer-events-none absolute inset-0 opacity-60" />

            <div className="relative text-center">
              <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-4 py-1.5 text-[11px] font-black tracking-[0.2em] text-amber-300">
                <Crown className="h-3.5 w-3.5" />
                {c.tag}
              </span>
              <h2 className="mx-auto mt-6 max-w-3xl font-display text-3xl font-black leading-tight text-white sm:text-5xl lg:text-6xl">
                {c.title}
              </h2>
              <p className="text-gradient mx-auto mt-4 max-w-3xl font-display text-xl font-extrabold sm:text-2xl lg:text-3xl">
                {c.subtitle}
              </p>
              <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-slate-400">{c.desc}</p>

              {/* live countdown */}
              <div className="mt-9 flex justify-center">
                <Countdown />
              </div>
            </div>

            {/* campaign visual */}
            <div className="relative mx-auto mt-12 max-w-4xl">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-violet-600/25 via-blue-600/25 to-cyan-500/20 blur-2xl" />
              <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-[0_20px_60px_rgba(2,6,23,0.7)]">
                <img
                  src={IMAGES[lang].campaign.src}
                  width={IMAGES[lang].campaign.w}
                  height={IMAGES[lang].campaign.h}
                  alt={c.subtitle}
                  className="block h-auto w-full"
                  loading="lazy"
                  data-testid="campaign-visual"
                />
              </div>
            </div>

            {/* steps */}
            <div className="relative mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {c.steps.map((s, i) => (
                <Reveal key={s.n} delay={i * 0.1}>
                  <div
                    data-testid={`campaign-step-${i}`}
                    className="group relative h-full rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur transition-colors duration-300 hover:border-blue-400/40"
                  >
                    <span className="text-gradient font-display text-3xl font-black">{s.n}</span>
                    <p className="mt-3 text-sm font-bold leading-snug text-slate-200">{s.t}</p>
                    <Lock className="absolute right-5 top-5 h-4 w-4 text-slate-600 transition-colors group-hover:text-blue-400" />
                  </div>
                </Reveal>
              ))}
            </div>

            {/* big line + CTA */}
            <div className="relative mt-14 text-center">
              <p className="text-gradient font-display text-3xl font-black tracking-tight sm:text-5xl">
                {c.bigLine}
              </p>
              <a
                href={waLink(t.contact.msgDefault)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="campaign-cta-btn"
                className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 px-10 py-4 text-base font-bold text-white shadow-[0_0_40px_rgba(99,102,241,0.5)] transition-transform duration-300 hover:scale-[1.05]"
              >
                <Lock className="h-5 w-5" />
                {c.cta}
                <ArrowRight className="h-4 w-4" />
              </a>
              <p className="mt-5 flex items-center justify-center gap-2 text-xs text-slate-500">
                <ChevronDown className="h-3.5 w-3.5" />
                {t.hero.lockLine}
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
